import FeatherIcon from "feather-icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  Badge,
  Spinner,
  Modal,
  Input,
  Alert,
} from "reactstrap";
import classnames from "classnames";
import api from "../helpers/api";
import PermissionGuard from "../components/Common/PermissionGuard";
import { formatRoleName } from "../constants/utils";

const STATUS_TABS = [
  { key: "ALL", label: "All" },
  { key: "PENDING", label: "Pending", color: "warning" },
  { key: "ACCEPTED", label: "Accepted", color: "success" },
  { key: "CANCELLED", label: "Cancelled", color: "secondary" },
  { key: "EXPIRED", label: "Expired", color: "danger" },
];

const Invitations = ({ refreshKey, onInvite }) => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeStatus, setActiveStatus] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [cancelModal, setCancelModal] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState("");

  const fetchInvitations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeStatus !== "ALL") {
        params.append("status", activeStatus);
      }
      const { data } = await api.get(`invitation/list?${params.toString()}`);
      setInvitations(data.response || []);
    } catch (error) {
      console.error("Error fetching invitations:", error);
    } finally {
      setLoading(false);
    }
  }, [activeStatus]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  // Refresh when parent triggers via refreshKey
  useEffect(() => {
    if (refreshKey > 0) {
      fetchInvitations();
    }
  }, [refreshKey, fetchInvitations]);

  const handleCancel = async () => {
    if (!selectedInvitation) return;
    setCancelLoading(true);
    setCancelError("");
    try {
      await api.delete(
        `invitation/cancel?invitationId=${selectedInvitation.id}`
      );
      setCancelModal(false);
      setSelectedInvitation(null);
      fetchInvitations();
    } catch (error) {
      console.error("Error cancelling invitation:", error);
      setCancelError(
        error?.response?.data?.message || "Failed to cancel invitation"
      );
    } finally {
      setCancelLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const tab = STATUS_TABS.find((t) => t.key === status);
    return (
      <Badge color={tab?.color || "secondary"} pill>
        {status}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filtered = invitations.filter((inv) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (inv.email || "").toLowerCase().includes(term) ||
      (inv.contactNumber || "").toLowerCase().includes(term) ||
      (inv.targetRole || "").toLowerCase().includes(term) ||
      (inv.invitedByName || "").toLowerCase().includes(term) ||
      (inv.organizationName || "").toLowerCase().includes(term)
    );
  });

  return (
    <>
      <div className="mb-4 d-flex align-items-center gap-3">
        {onInvite && (
          <PermissionGuard permission="invitation:create">
            <button
              type="button"
              className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2"
              onClick={onInvite}
            >
              <FeatherIcon icon="user-plus" size={18} />
              Invite User
            </button>
          </PermissionGuard>
        )}
        <div style={{ minWidth: "300px", maxWidth: "500px", flexGrow: 1 }}>
          <Input
            type="text"
            placeholder="Search invitations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <Card>
        <CardHeader>
          <Nav tabs className="nav-tabs-custom">
            {STATUS_TABS.map((tab) => (
              <NavItem key={tab.key}>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    active: activeStatus === tab.key,
                  })}
                  onClick={() => setActiveStatus(tab.key)}
                >
                  {tab.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </CardHeader>
        <CardBody>
          <div
            className="table-responsive"
            style={{
              maxHeight: "calc(100vh - 350px)",
              overflowY: "auto",
            }}
          >
            <table className="table table-hover mb-0">
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  background: "white",
                  zIndex: 1,
                }}
              >
                <tr>
                  <th>#</th>
                  <th>Recipient</th>
                  <th>Role</th>
                  <th>Organization</th>
                  <th>Status</th>
                  <th>Invited By</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {filtered.map((inv, idx) => (
                    <motion.tr
                      key={inv.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <td>{idx + 1}</td>
                      <td>
                        {inv.email || (
                          <span>
                            {inv.phoneCode ? `+${inv.phoneCode} ` : ""}
                            {inv.contactNumber}
                          </span>
                        )}
                      </td>
                      <td>{formatRoleName(inv.targetRole)}</td>
                      <td>{inv.organizationName || "-"}</td>
                      <td>{getStatusBadge(inv.status)}</td>
                      <td>{inv.invitedByName || "-"}</td>
                      <td>{formatDate(inv.expiresAt)}</td>
                      <td>
                        {inv.status === "PENDING" && (
                          <PermissionGuard permission="invitation:cancel">
                            <button
                              className="btn btn-soft-danger btn-sm"
                              onClick={() => {
                                setSelectedInvitation(inv);
                                setCancelModal(true);
                              }}
                              title="Cancel invitation"
                            >
                              <FeatherIcon icon="x-circle" size={14} />
                            </button>
                          </PermissionGuard>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
            {loading && (
              <div className="text-center py-3">
                <Spinner color="primary" />
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-3 text-muted">
                No invitations found
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Cancel Confirmation Modal */}
      <Modal isOpen={cancelModal} toggle={() => { setCancelModal(false); setCancelError(""); }} centered>
        <div className="modal-header">
          <h5 className="modal-title">Cancel Invitation</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setCancelModal(false)}
            aria-label="Close"
          />
        </div>
        <div className="modal-body">
          <p>
            Are you sure you want to cancel this invitation
            {selectedInvitation?.email
              ? ` to ${selectedInvitation.email}`
              : ""}
            ?
          </p>
          {cancelError && (
            <Alert color="danger">{cancelError}</Alert>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setCancelModal(false)}
          >
            No
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleCancel}
            disabled={cancelLoading}
          >
            {cancelLoading ? (
              <>
                <Spinner size="sm" className="me-1" /> Cancelling...
              </>
            ) : (
              "Yes, Cancel"
            )}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Invitations;
